<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from "vue";
import Preview from "../../../base-components/Preview";
import Button from "../../../base-components/Button/Button.vue";
import { useUsuariosStore } from "../../../services/usuarios";
import { useMensajesStore } from "../../../services/mensajes";
import { userUserStore } from "../../../stores/user";
import { useRegistrosTurnoStore } from "../../../services/registrosTurno";
import { TIPOS_REGISTRO_TURNO } from "../../../constants/tiposRegistroTurno";
import { Usuario, TipoRegistroTurno } from "@fixeduy/restaurantestypes";

const usuariosStore = useUsuariosStore();
const mensajesStore = useMensajesStore();
const userStore = userUserStore();

const pinIngresado = ref("");
const pinInputEl = ref<HTMLInputElement | null>(null);
const usuarioIdentificado = ref<Usuario | null>(null);
const cargando = ref(false);

onMounted(async () => {
  // Siempre traer usuarios frescos de Firestore (evita usar caché de otro entorno)
  await usuariosStore.getFromFirestore();
  await nextTick();
  if (!usuarioIdentificado.value) {
    pinInputEl.value?.focus();
  }
});
 
const digitosIngresados = computed(() => pinIngresado.value.length);
const pinCompleto = computed(() => digitosIngresados.value === 4);

/** Muestra _ para posiciones vacías y • para dígitos ingresados (privacidad). */
const pinDisplay = computed(() => {
  const len = pinIngresado.value.length;
  return "•".repeat(len) + "_".repeat(4 - len);
});

const agregarDigito = (d: string) => {
  if (pinIngresado.value.length < 4) {
    pinIngresado.value += d;
  }
};

const borrarDigito = () => {
  pinIngresado.value = pinIngresado.value.slice(0, -1);
};

const focusPinInput = async () => {
  await nextTick();
  pinInputEl.value?.focus();
};

const handlePinInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const digits = target.value.replace(/\D/g, "").slice(0, 4);
  pinIngresado.value = digits;
};

const validarPin = (): Usuario | null => {
  const pin = pinIngresado.value;
  const usuarios = usuariosStore.usuarios.filter(u => u.activo);
  const usuario = usuarios.find(u => u.pin === pin);
  return usuario ?? null;
};

const procesarPin = async () => {
  if (!pinCompleto.value) return;

  const usuario = validarPin();
  if (!usuario) {
    mensajesStore.mostrarToast(
      "PIN inválido",
      "El PIN no corresponde a ningún empleado o no está configurado. Contacta al administrador.",
      mensajesStore.mensajeType.error
    );
    pinIngresado.value = "";
    return;
  }

  usuarioIdentificado.value = usuario;
};

const reiniciar = () => {
  pinIngresado.value = "";
  usuarioIdentificado.value = null;
};

watch(usuarioIdentificado, async nuevo => {
  if (!nuevo) {
    await focusPinInput();
  }
});

const ultimoRegistro = ref<{ tipo: TipoRegistroTurno } | null>(null);
const cargandoUltimo = ref(false);

const cargarUltimoRegistro = async () => {
  if (!usuarioIdentificado.value) return;
  cargandoUltimo.value = true;
  try {
    const registro = await useRegistrosTurnoStore.obtenerUltimoRegistroTurno(
      usuarioIdentificado.value.id
    );
    ultimoRegistro.value = registro;
  } finally {
    cargandoUltimo.value = false;
  }
};

const accionesDisponibles = computed(() => {
  const ult = ultimoRegistro.value?.tipo ?? null;

  if (ult === null || ult === "salida") {
    return [{ tipo: "ingreso" as const, label: "Ingreso" }];
  }
  if (ult === "descansoInicio") {
    return [{ tipo: "descansoFin" as const, label: "Volver" }];
  }
  return [
    { tipo: "descansoInicio" as const, label: "Descanso" },
    { tipo: "salida" as const, label: "Salida" }
  ];
});

const ejecutarAccion = async (tipo: TipoRegistroTurno) => {
  if (!usuarioIdentificado.value) return;
  cargando.value = true;
  try {
    await useRegistrosTurnoStore.crearRegistro(
      usuarioIdentificado.value.id,
      usuarioIdentificado.value.nombre,
      tipo
    );
    const opt = TIPOS_REGISTRO_TURNO.find(t => t.value === tipo);
    mensajesStore.mostrarToast(
      "Registro guardado",
      `${opt?.label ?? tipo} registrado correctamente`,
      mensajesStore.mensajeType.exito
    );
    reiniciar();
  } catch (e) {
    mensajesStore.mostrarToast(
      "Error",
      "No se pudo guardar el registro. Intenta nuevamente.",
      mensajesStore.mensajeType.error
    );
  } finally {
    cargando.value = false;
  }
};

const handleProcesarPin = async () => {
  await procesarPin();
  if (usuarioIdentificado.value) {
    await cargarUltimoRegistro();
  }
};
</script>

<template>
  <Preview class="box mt-4">
    <div class="px-4 pt-4 pb-4">
      <h2 class="text-lg font-medium mb-4">Marcar turno</h2>

      <template v-if="!usuarioIdentificado">
        <div class="flex flex-col items-center">
          <p class="text-slate-600 dark:text-slate-400 mb-4">
            Ingresá tu PIN de 4 dígitos
          </p>
          <div
            class="grid place-items-center w-40 h-16 text-3xl font-mono tracking-[0.5em] border-2 rounded-xl mb-4"
            :class="
              pinCompleto
                ? 'border-primary bg-primary/10'
                : 'border-slate-300 dark:border-slate-600'
            "
            role="button"
            tabindex="0"
            @click="focusPinInput"
            @keydown.enter.prevent="focusPinInput"
          >
            {{ pinDisplay }}
          </div>

          <input
            ref="pinInputEl"
            type="password"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="4"
            autocomplete="one-time-code"
            :value="pinIngresado"
            @input="handlePinInput"
            class="absolute opacity-0 w-px h-px"
            aria-label="PIN de 4 dígitos"
          />

          <div class="grid grid-cols-3 gap-3 max-w-[200px] mb-4">
            <template v-for="n in 9" :key="n">
              <Button
                type="button"
                variant="outline-secondary"
                class="w-full h-14 text-xl font-mono"
                @click="agregarDigito(String(n)); focusPinInput()"
              >
                {{ n }}
              </Button>
            </template>
            <Button
              type="button"
              variant="outline-secondary"
              class="w-full h-14 text-xl"
              @click="agregarDigito('0'); focusPinInput()"
            >
              0
            </Button>
            <Button
              type="button"
              variant="outline-secondary"
              class="w-full h-14 text-xl"
              @click="borrarDigito(); focusPinInput()"
            >
              ←
            </Button>
            <Button
              type="button"
              variant="primary"
              class="w-full h-14 text-xl"
              :disabled="!pinCompleto || cargando"
              @click="handleProcesarPin"
            >
              OK
            </Button>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="flex flex-col items-center">
          <div class="mb-4">
            <p class="text-slate-600 dark:text-slate-400 text-center">
              Hola, <strong>{{ usuarioIdentificado.nombre }}</strong>
            </p>
          </div>

          <div v-if="cargandoUltimo" class="py-4 text-slate-500">
            Cargando...
          </div>
          <div v-else class="flex flex-wrap gap-3 justify-center">
            <Button
              v-for="a in accionesDisponibles"
              :key="a.tipo"
              type="button"
              variant="primary"
              class="min-h-[60px] px-6 text-lg"
              :disabled="cargando"
              @click="ejecutarAccion(a.tipo)"
            >
              {{ a.label }}
            </Button>
          </div>

          <Button
            type="button"
            variant="secondary"
            class="mt-4"
            @click="reiniciar"
          >
            Atrás
          </Button>
        </div>
      </template>
    </div>
  </Preview>
</template>
